import type { StatusTone } from "@/components/ui";
import type {
  ApprovalAction,
  ApprovalAuthorityLevel,
  ApprovalDecisionType,
  ApprovalStatus,
} from "./types";

export function formatLabel(value: string): string {
  return value.replaceAll("_", " ");
}

export function summarizeOpaqueObject(
  value: Record<string, unknown> | null | undefined,
  label = "object",
): string {
  if (value == null) return "—";
  const keys = Object.keys(value);
  if (keys.length === 0) return `${label}: empty`;
  return `${label}: ${keys.length} key(s) [${keys.join(", ")}] — values withheld`;
}

export function approvalStatusTone(status: ApprovalStatus): StatusTone {
  switch (status) {
    case "requested":
    case "pending":
      return "warning";
    case "approved":
      return "success";
    case "rejected":
      return "danger";
    case "returned_for_changes":
      return "info";
    case "withdrawn":
    case "expired":
      return "neutral";
    default:
      return "neutral";
  }
}

export const APPROVAL_ACTIONS_FROM: Record<
  ApprovalAction,
  readonly ApprovalStatus[]
> = {
  submit: ["requested"],
  withdraw: ["requested", "pending"],
  expire: ["requested", "pending"],
};

export function allowedApprovalActions(
  status: ApprovalStatus,
): ApprovalAction[] {
  return (Object.keys(APPROVAL_ACTIONS_FROM) as ApprovalAction[]).filter(
    (action) => APPROVAL_ACTIONS_FROM[action].includes(status),
  );
}

export function canRecordDecision(status: ApprovalStatus): boolean {
  return status === "requested" || status === "pending";
}

export const DECISION_OPTIONS: ApprovalDecisionType[] = [
  "approved",
  "rejected",
  "returned_for_changes",
  "abstained",
];

export const APPROVAL_TYPES = [
  "refund",
  "payout",
  "creator_tier",
  "business_verification",
  "enforcement_action",
  "campaign",
  "configuration",
  "maintenance_mode",
  "ownership_transfer",
  "future_governed_type",
] as const;

export const AUTHORITY_LEVELS: ApprovalAuthorityLevel[] = [
  "manager",
  "dual",
  "super_admin",
];

/** Best-effort HQ deep-link for known subject_type values. */
export function subjectHref(
  subjectType: string,
  subjectId: string,
): string | null {
  const map: Record<string, string> = {
    support_ticket: `/app/support/tickets/${subjectId}`,
    refund: `/app/finance/refunds/${subjectId}`,
    payout: `/app/finance/payouts/${subjectId}`,
    transaction: `/app/finance/transactions/${subjectId}`,
    settlement: `/app/finance/settlements/${subjectId}`,
    financial_exception: `/app/finance/exceptions/${subjectId}`,
    financial_dispute: `/app/finance/disputes/${subjectId}`,
    commission_rule: `/app/finance/commission-rules/${subjectId}`,
    business: `/app/businesses/${subjectId}`,
    bookable_resource: `/app/businesses/resources/${subjectId}`,
    booking: `/app/bookings/${subjectId}`,
    community: `/app/communities/${subjectId}`,
    creator_profile: `/app/creators/profiles/${subjectId}`,
    creator_application: `/app/creators/applications/${subjectId}`,
    report: `/app/trust-safety/reports/${subjectId}`,
    moderation_case: `/app/trust-safety/cases/${subjectId}`,
    appeal: `/app/trust-safety/appeals/${subjectId}`,
    notification: `/app/marketing/campaigns/${subjectId}`,
    marketplace_signal: `/app/marketplace/signals/${subjectId}`,
    marketplace_opportunity: `/app/marketplace/opportunities/${subjectId}`,
    platform_configuration: `/app/system/configurations/${subjectId}`,
    feature_flag: `/app/system/feature-flags/${subjectId}`,
    maintenance_mode: `/app/system/maintenance/${subjectId}`,
    user: `/app/users/${subjectId}`,
    task: `/app/tasks/${subjectId}`,
  };
  return map[subjectType] ?? null;
}

export function authorityLevelsForMin(
  min?: ApprovalAuthorityLevel,
): string | undefined {
  if (!min || min === "manager") return undefined;
  if (min === "dual") return "dual,super_admin";
  return "super_admin";
}
